// @flow

import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import profileActionCreators from "@modules/profile/actionCreator";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  TabBar,
  View,
  ConfirmationModal,
} from "@components";
import { MyEmployeesList, MyInformation } from "@modules/profile/components";
import type { MyDetails } from "@modules/profile/reducer/my-details/types";
import type { Profile } from "@modules/account/types";
import type { PaginationExtendedType } from "@types";
import type { State } from "@store/reducers";
import * as profileSelectors from "@modules/profile/selectors";
import * as accountSelectors from "@modules/account/selectors";
import { getCurrentPath } from "@selectors";
import {
  getAddNotificationModal,
  getEmployeesNotificationAuditList,
  isLoading as sharedIsLoading,
} from "@modules/shared/selectors";
import { getUser, getUserClientId } from "@modules/account/selectors";
import * as employeeIdentityDataSelectors from "@modules/service/selectors";
import sharedActionCreators from "@modules/shared/actionCreator";
import serviceActionCreators from "@modules/service/actionCreator";
import type { NotificationContact } from "@modules/profile/types";
import type {
  AddNotificationModal,
  NotificationAuditListType,
} from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeeIdentity } from "@modules/service/reducer/employee-identity/types";

type PropsFromState = $Exact<{
  isLoading: boolean,
  contactInfoSchema: Object | null,
  myDetails: MyDetails | null,
  profile: ?Profile,
  currentPath: string,
  pagination: ?PaginationExtendedType,
  allowEmployeeAudit: boolean,

  // employees
  myEmployees: Array<NotificationContact>,
  pagination2: PaginationExtendedType,
  sharedIsLoading: boolean,
  selectedEmployeeId: ?number,
  clientId: ?number,
  userProfile: ?Object,
  employeesNotificationAuditList: NotificationAuditListType,
  employeeIdentityData: EmployeeIdentity,
  modal: AddNotificationModal,
}>;

type PropsFromDispatch = $Exact<{
  getContactInfoSchema: typeof profileActionCreators.GetContactInfoSchema,
  getMyDetails: typeof profileActionCreators.GetMyDetails,

  // employees
  getNotificationContacts: typeof profileActionCreators.GetNotificationContacts,
  toggleAddNotificationModal: typeof sharedActionCreators.ToggleAddNotificationModal,
  notificationAuditRemoveAccessToRecord: typeof sharedActionCreators.NotificationAuditRemoveAccessToRecord,
  notificationAuditComplete: typeof sharedActionCreators.NotificationAuditComplete,
  setSelectedEmployeeId: typeof profileActionCreators.SetNotificationContactsSelectedEmployeeId,
  searchNotificationAuditByStatus: typeof sharedActionCreators.SearchNotificationAuditByStatus,
  updateNotificationAuditStatus: typeof sharedActionCreators.UpdateNotificationAuditStatus,
  getEmployeeIdentityData: typeof serviceActionCreators.GetEmployeeIdentityData,
}>;

type Props = $Exact<{
  onCloseHandler: () => void,
  onSubmit: (form: Object) => void,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

function UserInfoModal(props: Props) {
  useEffect(() => {
    props.getMyDetails();
    if (!props.contactInfoSchema) props.getContactInfoSchema();

    props.getEmployeeIdentityData({
      clientId: props.clientId,
    });

    if (props.allowEmployeeAudit) {
      // employees
      props.getNotificationContacts({});

      props.searchNotificationAuditByStatus({
        managerId: (props.userProfile || {}).id,
        clientId: (props.userProfile || {}).clientId,
        status: ["ONGOING", "PENDING"],
      });
    }
  }, []);

  function handlePaginationChange(page: number) {
    props.getNotificationContacts({
      page,
    });
  }
  function notificationAuditRemoveAccessToRecord(props: Object) {
    // props.notificationAuditRemoveAccessToRecord({
    //   clientId: props.clientId,
    //   auditId: this.notificationAuditId,
    //   individualNotificationId: props.individualNotificationId,
    // });
  }

  return (
    <Modal className="modal-large">
      <ModalHeader toggle={props.onCloseHandler}>{props.t("PROFILE")}</ModalHeader>
      <ModalBody>
        <View>
          {props.allowEmployeeAudit ? (
            <TabBar noHash={true}>
              <TabBar.Tab title={props.t("MY_DETAILS")}>
                {props.myDetails && (
                  <MyInformation
                    myDetails={props.myDetails}
                    profile={props.profile}
                    contactInfoSchema={props.contactInfoSchema}
                  />
                )}
              </TabBar.Tab>
              <TabBar.Tab title={props.t("MY_EMPLOYEES")}>
                <MyEmployeesList
                  clientId={props.clientId}
                  selectedEmployeeId={props.selectedEmployeeId}
                  data={props.myEmployees}
                  pagination={props.pagination}
                  handlePaginationChange={handlePaginationChange}
                  setSelectedEmployeeId={props.setSelectedEmployeeId}
                  toggleAddNotificationModal={props.toggleAddNotificationModal}
                  showAddNotificationModal={props.modal.visible}
                  loadNotificationContacts={props.getNotificationContacts}
                  notificationAuditRemoveAccessToRecord={notificationAuditRemoveAccessToRecord}
                  notificationAuditRequiresRemoval={false}
                  userType={props.userProfile?.userType}
                />
                {/*{this.state.showNotificationAuditThankYouModal && (*/}
                {/*  <ConfirmationModal*/}
                {/*    headerText={props.t("NOTIFICATION_AUDIT")}*/}
                {/*    bodyContent={props.t("THANK_YOU_AUDIT_MODAL_TEXT", {*/}
                {/*      numDays: props.employeeIdentityData.notificationAuditFrequency,*/}
                {/*    })}*/}
                {/*    confirmBtnLabel={props.t("CLOSE")}*/}
                {/*    onConfirm={this.toggleNotificationAuditThankYouModal}*/}
                {/*  />*/}
                {/*)}*/}
              </TabBar.Tab>
            </TabBar>
          ) : (
            <MyInformation
              myDetails={props.myDetails}
              profile={props.profile}
              contactInfoSchema={props.contactInfoSchema}
            />
          )}
        </View>
      </ModalBody>
      {/*<ModalFooter className="d-flex justify-content-end">*/}
      {/*  <Button color="secondary" onClick={props.onDismissHandler}>*/}
      {/*    {props.t("CANCEL")}*/}
      {/*  </Button>*/}
      {/*  <Button materialIcon="save" type="submit" onClick={this.submitForm}>*/}
      {/*    {props.t("SAVE")}*/}
      {/*  </Button>*/}
      {/*</ModalFooter>*/}
    </Modal>
  );
}
const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: profileSelectors.isLoading(state),
    contactInfoSchema: profileSelectors.getContactInfoSchema(state),
    myDetails: profileSelectors.getMyDetails(state),
    profile: accountSelectors.getProfile(state),
    currentPath: getCurrentPath(state),
    pagination: profileSelectors.getMyAbsencesPagination(state),
    allowEmployeeAudit: accountSelectors.getUserAllowEmployeeAudit(state),

    // employees
    myEmployees: profileSelectors.getMyEmployees(state),
    pagination2: profileSelectors.getMyEmployeesPagination(state),
    selectedEmployeeId: profileSelectors.getSelectedEmployeeId(state),
    sharedIsLoading: sharedIsLoading(state),
    clientId: getUserClientId(state),
    userProfile: getUser(state),
    employeesNotificationAuditList: getEmployeesNotificationAuditList(state),
    employeeIdentityData: employeeIdentityDataSelectors.getEmployeeIdentityData(state),
    modal: getAddNotificationModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getContactInfoSchema: profileActionCreators.GetContactInfoSchema,
  getMyDetails: profileActionCreators.GetMyDetails,

  // employees
  getNotificationContacts: profileActionCreators.GetNotificationContacts,
  toggleAddNotificationModal: sharedActionCreators.ToggleAddNotificationModal,
  notificationAuditRemoveAccessToRecord: sharedActionCreators.NotificationAuditRemoveAccessToRecord,
  notificationAuditComplete: sharedActionCreators.NotificationAuditComplete,
  setSelectedEmployeeId: profileActionCreators.SetNotificationContactsSelectedEmployeeId,
  searchNotificationAuditByStatus: sharedActionCreators.SearchNotificationAuditByStatus,
  updateNotificationAuditStatus: sharedActionCreators.UpdateNotificationAuditStatus,
  getEmployeeIdentityData: serviceActionCreators.GetEmployeeIdentityData,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(UserInfoModal);
